import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { getAuthHeaders, setAuth, getUser, API_URL } from '../../utils/auth';
import { toast } from '../ui/sonner';
import {
  Building2,
  ChevronDown,
  Check,
  RefreshCw
} from 'lucide-react';

const API = API_URL;

const TenantSwitcher = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [tenants, setTenants] = useState([]);
  const [activeTenant, setActiveTenant] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (user) {
      fetchUserTenants();
    }
  }, []);

  const fetchUserTenants = async () => {
    try {
      const response = await axios.get(`${API}/user/tenants`, {
        headers: getAuthHeaders()
      });
      
      setTenants(response.data.tenants || []);
      
      // Find active tenant
      const active = response.data.tenants.find(
        t => t.id === response.data.active_tenant_id
      );
      setActiveTenant(active);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      // Don't show error toast, user might be in a page that doesn't need tenants
    }
  };

  const handleSwitchTenant = async (tenantId) => {
    if (activeTenant?.id === tenantId) {
      setShowDropdown(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${API}/user/switch-tenant`,
        { tenant_id: tenantId },
        { headers: getAuthHeaders() }
      );

      // Update auth with new token
      const newToken = response.data.token;
      const updatedUser = {
        ...user,
        active_tenant_id: tenantId
      };

      setAuth(newToken, updatedUser);
      
      toast.success(`Switched to ${response.data.tenant_name}`);
      
      setShowDropdown(false);
      
      // Refresh the page to update context
      window.location.reload();
    } catch (error) {
      console.error('Error switching tenant:', error);
      toast.error(error.response?.data?.detail || 'Failed to switch tenant');
    } finally {
      setLoading(false);
    }
  };

  // Only show for superadmin with multiple tenants (cross-tenant visibility)
  if (!user || user.role !== 'superadmin' || tenants.length <= 1) {
    return null;
  }

  return (
    <div className="relative">
      {/* Trigger Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center gap-2 max-w-[200px]"
        disabled={loading}
      >
        <Building2 className="h-4 w-4" />
        <span className="truncate">
          {activeTenant?.name || 'Select Tenant'}
        </span>
        <ChevronDown className="h-4 w-4" />
      </Button>

      {/* Dropdown Menu */}
      {showDropdown && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setShowDropdown(false)}
          />
          
          {/* Menu */}
          <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-20 max-h-96 overflow-auto">
            <div className="p-3 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase">
                Switch Organization
              </p>
            </div>

            <div className="py-2">
              {tenants.map((tenant) => (
                <button
                  key={tenant.id}
                  onClick={() => handleSwitchTenant(tenant.id)}
                  disabled={loading}
                  className={`w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition ${
                    activeTenant?.id === tenant.id ? 'bg-blue-50' : ''
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Building2 className="h-4 w-4 text-white" />
                    </div>
                    <div className="text-left min-w-0">
                      <p className="font-medium text-sm truncate">
                        {tenant.name}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">
                          {tenant.user_role || tenant.subscription_tier}
                        </Badge>
                        {tenant.is_primary && (
                          <span className="text-xs text-blue-600">Primary</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {activeTenant?.id === tenant.id && (
                    <Check className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>

            {loading && (
              <div className="p-3 border-t border-gray-100 bg-gray-50">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span>Switching tenant...</span>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TenantSwitcher;
